import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Euro,
  ShieldAlert,
  UserX,
  Users,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type RiskListKind, matchesRiskListKind } from "./RiskListModal.js";

const DAY_MS = 24 * 60 * 60 * 1000;

interface TrendData {
  available: boolean;
  since?: string;
  points: { date: string; avgScore: number; highRiskCount: number }[];
}

interface EmployeeRow {
  id: number;
  displayName: string;
  riskLevel: "high" | "medium" | "low" | null;
  // Optional wie in api.ts (EmployeeRecord.isConfirmedLeaving?)
  isConfirmedLeaving?: boolean;
}

/** Delta aktueller Punkt vs. Punkt ~7 Tage davor (nächstliegender Schnappschuss). */
function weekDelta(points: TrendData["points"], pick: (p: TrendData["points"][number]) => number) {
  if (points.length < 2) return null;
  const last = points[points.length - 1]!;
  const target = new Date(last.date).getTime() - 7 * DAY_MS;
  const ref = points.reduce((best, p) =>
    Math.abs(new Date(p.date).getTime() - target) < Math.abs(new Date(best.date).getTime() - target)
      ? p
      : best,
  );
  if (ref.date === last.date) return null;
  return { value: pick(last) - pick(ref), refDate: ref.date };
}

function DeltaChip({ value, refDate, unit }: { value: number; refDate: string; unit?: string }) {
  const { t } = useTranslation();
  const stable = Math.abs(value) < 0.05;
  const ref = new Date(refDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return (
    <span
      className="chip"
      title={t("cockpit.kpi.deltaTooltip", { date: ref })}
      style={{
        background: stable
          ? "var(--bg-subtle)"
          : value > 0
            ? "var(--risk-high-bg)"
            : "var(--risk-low-bg)",
        color: stable
          ? "var(--text-muted)"
          : value > 0
            ? "var(--risk-high-text)"
            : "var(--risk-low-text)",
      }}
    >
      {stable ? (
        t("cockpit.kpi.stable")
      ) : (
        <>
          {value > 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
          {`${value > 0 ? "+" : ""}${Math.abs(value).toFixed(value % 1 === 0 ? 0 : 1)}${unit ?? ""}`}
        </>
      )}
    </span>
  );
}

function Tile(props: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
  chip?: React.ReactNode;
  sub?: string;
  names?: string[];
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const names = props.names ?? [];
  const clickable = Boolean(props.onClick);
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={props.onClick}
      className={`card ${clickable ? "card-hover" : ""} flex-1 min-w-[150px] px-4 py-3 text-left relative`}
      style={{ borderLeft: `2px solid ${props.color}`, cursor: clickable ? "pointer" : "default" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {hover && names.length > 0 && (
        <div
          className="absolute left-2 top-full mt-1 z-20 p-2.5 min-w-[180px] max-w-[260px]"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          {names.slice(0, 12).map((n) => (
            <div key={n} className="text-[11px] py-0.5" style={{ color: "var(--text-secondary)" }}>
              {n}
            </div>
          ))}
          {names.length > 12 && (
            <div className="text-[10px] pt-1" style={{ color: "var(--text-muted)" }}>
              {t("cockpit.kpi.moreNames", { count: names.length - 12 })}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 mb-1" style={{ color: props.color }}>
        {props.icon}
        <span
          className="text-[10px] uppercase tracking-[0.1em] font-semibold"
          style={{ color: "var(--text-muted)" }}
        >
          {props.label}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {props.value === "…" ? (
          <span className="skeleton inline-block h-5 w-12" aria-hidden />
        ) : (
          <span
            className="text-xl font-bold font-mono-data leading-none"
            style={{ color: "var(--text-primary)" }}
          >
            {props.value}
          </span>
        )}
        {props.chip}
      </div>
      {props.sub && (
        <div className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
          {props.sub}
        </div>
      )}
    </button>
  );
}

export function KpiHeader(props: {
  summary?: {
    total: number;
    high: number;
    medium: number;
    // Optional wie in api.ts (EmployeeSummary.confirmedLeaving?)
    confirmedLeaving?: number;
    avgScore: number;
  };
  employees: EmployeeRow[];
  trend?: TrendData;
  cost?: {
    totalExpectedCost: number;
    employeeCount: number;
    /** Was die Brücke Salesforce→Personio nicht zuordnen konnte (F14/F16/F17). */
    billingSummary?: {
      unassignedAmount: number;
      unassignedCount: number;
      formerAmount: number;
      formerCount: number;
      externalAmount: number;
      externalCount: number;
      excludedAmount: number;
      excludedCount: number;
    };
  };
  loading: boolean;
  onOpenList: (kind: RiskListKind) => void;
}) {
  const { t } = useTranslation();
  const s = props.summary;
  const v = (n: number | undefined) => (props.loading || n === undefined ? "…" : String(n));
  // Backend kann confirmedLeaving weglassen → wie im alten Dashboard als 0 werten
  const leaving = s ? (s.confirmedLeaving ?? 0) : undefined;
  const points = props.trend?.available ? props.trend.points : [];
  const dHigh = weekDelta(points, (p) => p.highRiskCount);
  const dAvg = weekDelta(points, (p) => Math.round(p.avgScore * 100));
  const namesFor = (kind: RiskListKind) =>
    props.employees.filter((e) => matchesRiskListKind(e, kind)).map((e) => e.displayName);
  const fmtEur = (n: number) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);

  const bs = props.cost?.billingSummary;

  return (
    <>
      <div className="flex flex-wrap gap-4 mb-5 stagger">
        <Tile
          icon={<Users size={13} />}
          label={t("cockpit.kpi.active")}
          value={v(s?.total)}
          color="var(--text-secondary)"
        />
        <Tile
          icon={<ShieldAlert size={13} />}
          label={t("cockpit.kpi.high")}
          value={v(s?.high)}
          color="var(--risk-high)"
          chip={dHigh ? <DeltaChip value={dHigh.value} refDate={dHigh.refDate} /> : undefined}
          names={namesFor("high")}
          onClick={s ? () => props.onOpenList("high") : undefined}
        />
        <Tile
          icon={<AlertTriangle size={13} />}
          label={t("cockpit.kpi.medium")}
          value={v(s?.medium)}
          color="var(--risk-medium)"
          names={namesFor("medium")}
          onClick={s ? () => props.onOpenList("medium") : undefined}
        />
        <Tile
          icon={<UserX size={13} />}
          label={t("cockpit.kpi.leaving")}
          value={v(leaving)}
          color={(leaving ?? 0) > 0 ? "var(--risk-high)" : "var(--text-muted)"}
          names={namesFor("confirmedLeaving")}
          onClick={(leaving ?? 0) > 0 ? () => props.onOpenList("confirmedLeaving") : undefined}
        />
        <Tile
          icon={<Activity size={13} />}
          label={t("cockpit.kpi.avgScore")}
          value={s ? `${Math.round(s.avgScore * 100)}%` : "…"}
          color={
            s
              ? s.avgScore >= 0.6
                ? "var(--risk-high)"
                : s.avgScore >= 0.4
                  ? "var(--risk-medium)"
                  : "var(--risk-low)"
              : "var(--text-muted)"
          }
          chip={
            dAvg ? <DeltaChip value={dAvg.value} refDate={dAvg.refDate} unit="pp" /> : undefined
          }
        />
        <Tile
          icon={<Euro size={13} />}
          label={t("cockpit.kpi.cost")}
          value={props.cost ? fmtEur(props.cost.totalExpectedCost) : "—"}
          color="var(--accent-gold)"
          sub={
            props.cost ? t("cockpit.kpi.costSub", { count: props.cost.employeeCount }) : undefined
          }
        />
      </div>
      {/* Was sich keinem Menschen zuordnen ließ — bewusst sichtbar. Die Kachel
        oben stand wegen F14 dauerhaft auf 0 €, ohne dass irgendwo ein Hinweis
        darauf zu sehen war. Erscheint nur, wenn es etwas zu melden gibt. */}
      {/* Nur diese eine Zeile, bewusst (Marie, 28.08.2026): Das Tool dient den
          eigenen Mitarbeitern. Ehemalige und Externe sind eine Controlling-Frage
          und werden nicht angezeigt — die Kategorien bleiben aber im Programm,
          damit fremder Umsatz nicht in die Mitarbeiterzahlen rutscht.

          Diese Zeile ist kein Controlling, sondern Schutz für die eigenen Zahlen:
          Heiratet eine Mitarbeiterin und heißt in Personio anders als in
          Salesforce, findet das Tool ihren Umsatz nicht mehr. Ihre Zahl fiele
          still auf null und ihre Fluktuationskosten sähen harmlos aus. Genau
          dann springt sie an — sonst steht sie auf null und bleibt unsichtbar. */}
      {bs && bs.unassignedAmount > 0 && (
        <div className="text-[10px] -mt-3 mb-5" style={{ color: "var(--risk-high)" }}>
          Nicht zugeordnet: {fmtEur(bs.unassignedAmount)} · {bs.unassignedCount} Salesforce-Konten
          ohne Personio-Treffer. Prüfen, ob dahinter ein Mitarbeiter mit geändertem Namen steckt.
        </div>
      )}
    </>
  );
}
